import { motion } from 'framer-motion'
import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { BuildingFilled } from 'src/shared/components/icons/building-filled'
import TabButtonIcon from 'src/shared/components/ui/navigation/tab-button-icon'
import { useSignOut } from 'src/shared/features/auth/api'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { LogoutIcon } from 'src/shared/features/users/components/marketplace/icons/logout'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesDefault } from 'src/shared/utils/tailwind-styles'

import AppWindowFrame from './navigation/app-window-frame'
import type { TabButtonConfig } from './navigation/app-window-frame'

const tabButtons: TabButtonConfig[] = [
  // TODO: Add the other tabs here when we add them back in
  // {
  //   iconName: 'house',
  //   name: 'Overview',
  //   path: paths.enterprise.dashboard.overview.path,
  // },
  {
    iconName: 'people',
    name: 'Directory',
    path: paths.enterprise.dashboard.directory.path,
  },
  // TODO: Add the other tabs here when we add them back in
  // {
  //   iconName: 'document',
  //   name: 'Billing',
  //   path: paths.enterprise.dashboard.billing.path,
  // },
  // {
  //   iconName: 'bell',
  //   name: 'Activity',
  //   path: paths.enterprise.dashboard.activity.path,
  // },
]

interface EnterpriseDashboardLayoutProps {
  children: React.ReactNode
}

export function EnterpriseDashboardLayout({ children }: EnterpriseDashboardLayoutProps) {
  const location = useLocation()
  const { isMobile } = useBreakpoint()
  const { data: currentUser } = useMyUserData()
  const { bpSwitch } = useBreakpoint()
  const signOut = useSignOut({ isEnterprise: true })

  const handleLogout = () => {
    signOut.mutateAsync()
  }

  const activeTabIdx = useMemo(() => {
    if (
      location.pathname.startsWith(paths.enterprise.dashboard.directory.path) ||
      location.pathname === paths.enterprise.dashboard.path
    ) {
      return 0
    }
    // TODO: Add the other tabs here when we add them back in
    // if (
    //   location.pathname.startsWith(paths.enterprise.dashboard.overview.path) ||
    //   location.pathname === paths.enterprise.dashboard.path
    // ) {
    //   return 0
    // }
    // if (location.pathname.startsWith(paths.enterprise.dashboard.directory.path)) {
    //   return 1
    // }
    // if (location.pathname.startsWith(paths.enterprise.dashboard.billing.path)) {
    //   return 2
    // }
    // if (location.pathname.startsWith(paths.enterprise.dashboard.activity.path)) {
    //   return 3
    // }
    return -1
  }, [location.pathname])

  return (
    <>
      <meta name="theme-color" content="#1B3029" />
      {isMobile ? (
        <EnterpriseDashboardMobileLayout tabButtons={tabButtons} selectedTab={activeTabIdx}>
          {children}
        </EnterpriseDashboardMobileLayout>
      ) : (
        <AppWindowFrame
          menuLabel="Switch"
          menuComponent={
            <div className="relative">
              {/* Company Logo */}
              <div className="box-content flex size-[50px] shrink-0 items-center justify-center rounded-full border-[3px] border-white bg-muted">
                <BuildingFilled size={23} className="text-[#D8D8D6]" />
              </div>

              {/* User Avatar */}
              <div className="pointer-events-none absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 select-none">
                <ProfileAvatar
                  size={bpSwitch({
                    md: 27,
                    default: 23,
                  })}
                  userId={currentUser?.id ?? -1}
                  className="box-content shrink-0 border-[1.5px] border-white"
                />
              </div>
            </div>
          }
          tabButtons={tabButtons}
          logoLink={paths.enterprise.dashboard.path}
          activeTabIdx={activeTabIdx}
          showLogoutButton={true}
          logoutButtonOnClick={handleLogout}
        >
          {children}
        </AppWindowFrame>
      )}
    </>
  )
}

interface EnterpriseDashboardMobileLayoutProps {
  children: React.ReactNode
  tabButtons: TabButtonConfig[]
  selectedTab: number
}

function EnterpriseDashboardMobileLayout({ children, tabButtons, selectedTab }: EnterpriseDashboardMobileLayoutProps) {
  const signOut = useSignOut({ isEnterprise: true })

  const handleLogout = () => {
    signOut.mutateAsync()
  }

  return (
    <div className="flex max-h-screen w-full flex-col overflow-hidden">
      <div
        className={cn(
          'w-full flex-1 overflow-y-auto',
          ScrollBarTailwindClassesDefault,
          '[&::-webkit-scrollbar-track]:my-0',
        )}
      >
        {children}
      </div>
      <div className="h-[75px] w-full shrink-0 bg-transparent"></div>
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-20 h-[75px]',
          'border-t-[0.5px] border-[#EFEFEF] bg-white shadow-[0_-3px_12px_0_rgba(27,48,41,0.05)]',
        )}
      >
        <div className="flex w-full flex-row items-center justify-between px-5 pb-[20px]">
          {tabButtons.map((tabButton, index) => (
            <Link to={tabButton.path} key={tabButton.name} className="relative flex-1">
              {selectedTab === index && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-x-0 top-0 h-[2px] rounded-b-[2px] bg-primary"
                />
              )}
              <div
                className={cn(
                  'flex flex-col items-center justify-center gap-[6px] pt-[15px] text-muted',
                  selectedTab === index && 'text-primary',
                )}
              >
                <TabButtonIcon name={tabButton.iconName} className="size-[20px]" />
                <span className="text-[10px] font-medium leading-none">{tabButton.name}</span>
              </div>
            </Link>
          ))}
          <div className="relative flex-1">
            <div className={cn('flex flex-col items-center justify-center gap-[6px] pt-[15px] text-muted')}>
              <div className="box-content flex size-[24px] shrink-0 items-center justify-center rounded-full border-[3px] border-white bg-muted">
                <BuildingFilled size={10} className="text-[#D8D8D6]" />
              </div>
              <span className="text-[10px] font-medium leading-none">Menu</span>
            </div>
          </div>
          {/* Temp Logout Button */}
          <div className="relative flex-1 cursor-pointer" onClick={() => handleLogout()}>
            <div className={cn('flex flex-col items-center justify-center gap-[6px] pt-[15px] text-muted')}>
              <div className="box-content flex size-[24px] shrink-0 items-center justify-center rounded-full border-[3px] border-white bg-muted">
                <LogoutIcon className="size-[10px]" color="#D8D8D6" />
              </div>
              <span className="text-[10px] font-medium leading-none">Log out</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
